import React from 'react';
import { gpuAPI } from '../../services/api';

const STATUS_COLOR = {
  available:   { bg: 'var(--color-success-50)', color: 'var(--color-success-500)', label: 'Available' },
  provisioned: { bg: 'var(--brand-soft)', color: 'var(--brand)', label: 'In Use' },
  maintenance: { bg: 'var(--color-warning-50)', color: 'var(--color-warning-500)', label: 'Maintenance' },
  running:     { bg: 'var(--color-success-50)', color: 'var(--color-success-500)', label: 'Running' },
  stopped:     { bg: 'var(--bg)', color: 'var(--muted)', label: 'Stopped' },
  pending:     { bg: 'var(--accent-soft)', color: 'var(--accent)', label: 'Pending' },
  terminated:  { bg: 'var(--color-danger-50)', color: 'var(--color-danger-500)', label: 'Terminated' },
  // 'unknown' = the GPU-server agent is offline, so we can't verify the pod.
  unknown:     { bg: 'var(--color-danger-50)', color: 'var(--color-danger-500)', label: 'Unreachable' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLOR[status] || { bg: 'var(--bg)', color: 'var(--muted)', label: status };
  return (
    <span title={status === 'unknown' ? "Agent offline — live status can't be verified" : undefined}
      style={{ background: s.bg, color: s.color, padding: '3px 10px',
      borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{s.label}</span>
  );
}

function GpuBar({ pct, label }) {
  const color = pct < 60 ? 'var(--color-success-500)' : pct < 85 ? 'var(--accent)' : 'var(--color-danger-500)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
      <span style={{ color: 'var(--muted)', width: 28 }}>{label}</span>
      <div style={{ flex: 1, height: 6, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct,100)}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ color, fontWeight: 600, width: 36 }}>{pct}%</span>
    </div>
  );
}

// Sub-component: instance list per GPU server
function GpuInstanceList({ gpuServerId, onAllocate, canCreate }) {
  const [instances, setInstances] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const [busyId, setBusyId] = React.useState(null);

  const load = async () => {
    try {
      const res = await gpuAPI.listInstances(gpuServerId);
      setInstances(res.data);
      setLoaded(true);
    } catch (e) {}
  };

  const toggle = () => {
    if (!open && !loaded) load();
    setOpen(!open);
  };

  // Reserve = mark maintenance (provisioner skips it); Release = back to available.
  const setReserved = async (inst, reserve) => {
    setBusyId(inst.id);
    try {
      await gpuAPI.updateInstance(gpuServerId, inst.id, { status: reserve ? 'maintenance' : 'available' });
      await load();
    } catch (e) {
      alert('Failed to ' + (reserve ? 'reserve' : 'release') + ' GPU: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="gpu-instances-section">
      <button className="gpu-instances-toggle" onClick={toggle}>
        {open ? '▾' : '▸'} Instances {loaded ? `(${instances.length})` : ''}
      </button>
      {open && (
        <div className="gpu-instances-list">
          {instances.map(inst => (
            <div key={inst.id} className="gpu-instance-row">
              <div>
                <span className="gpu-inst-name">{inst.name}</span>
                <span className="gpu-inst-type">{inst.instance_type}</span>
                <span className="gpu-inst-vram">{inst.vram_gb}GB</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StatusBadge status={inst.status} />
                {inst.status === 'available' && canCreate && (
                  <button className="btn btn-primary btn-sm"
                    onClick={() => onAllocate(inst.id, inst.name, inst.vram_gb)}>
                    Allocate
                  </button>
                )}
                {/* Reserve keeps a GPU out of the provisioning pool (e.g. used by an
                    external app). Only available/maintenance GPUs can be toggled — a
                    provisioned (in-use) GPU must be freed first. */}
                {inst.status === 'available' && (
                  <button className="button-secondary" disabled={busyId === inst.id}
                    style={{ fontSize: 11, padding: '3px 10px', color: '#d69e2e', borderColor: '#fefcbf' }}
                    title="Reserve this GPU so SarvKube never provisions onto it"
                    onClick={() => setReserved(inst, true)}>
                    {busyId === inst.id ? '…' : 'Reserve'}
                  </button>
                )}
                {inst.status === 'maintenance' && (
                  <button className="button-secondary" disabled={busyId === inst.id}
                    style={{ fontSize: 11, padding: '3px 10px', color: '#38a169', borderColor: '#c6f6d5' }}
                    title="Release this GPU back into the provisioning pool"
                    onClick={() => setReserved(inst, false)}>
                    {busyId === inst.id ? '…' : 'Release'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function GpuServers({ gpuServers, loading, onRefresh, onRegister, onDelete, onReleaseStuck, onAllocate, isAdmin, canCreate, canEdit, canDelete }) {
  // Live monitoring: when ON, poll each server's metrics (nvtop-style).
  // FIX (Live Monitor was flapping the API): previously this fired onRefresh for
  // ALL servers in PARALLEL every 2.5s on a fixed interval. On the single-worker
  // API that burst of blocking nvidia-smi agent calls saturated the event loop →
  // /health timed out → liveness probe killed the pod → 503 flap. Now:
  //   • poll servers SEQUENTIALLY (await each before the next) — no burst,
  //   • self-schedule the NEXT cycle only AFTER the current finishes (no stacking),
  //   • longer gap (4s), and reload the list ONCE per cycle (skipReload on each).
  const [live, setLive] = React.useState(false);
  React.useEffect(() => {
    if (!live) return;
    let stopped = false;
    let timer = null;
    const cycle = async () => {
      if (stopped) return;
      for (const gs of gpuServers) {
        if (stopped) return;
        try { await onRefresh(gs.id, true); } catch (e) { /* keep going */ }
      }
      if (stopped) return;
      try { await onRefresh(null, false); } catch (e) {}   // single reload after the batch
      if (!stopped) timer = setTimeout(cycle, 4000);
    };
    cycle();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [live, gpuServers, onRefresh]);


  return (
    <div>
      {gpuServers.length > 0 && (
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
          <button className="button-secondary"
            onClick={() => setLive(v => !v)}
            style={{ fontSize:12, padding:'5px 14px', fontWeight:600,
              color: live ? '#e53e3e' : '#38a169',
              borderColor: live ? '#fed7d7' : '#c6f6d5' }}
            title="Auto-refresh GPU metrics every 2.5s">
            {live ? '● LIVE — stop' : '○ Live monitor'}
          </button>
        </div>
      )}
      {loading && <div className="resource-loading">Loading GPU servers...</div>}
      {!loading && gpuServers.length === 0 && (
        <div className="gpu-empty">
          <div style={{ fontSize: 48 }}>🖥</div>
          <div>No GPU servers registered yet.</div>
          {isAdmin && canCreate && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={onRegister}>
              Register your first GPU server
            </button>
          )}
        </div>
      )}
      <div className="gpu-server-grid">
        {gpuServers.map(gs => {
          const metrics = gs.gpu_metrics || [];
          return (
            <div key={gs.id} className="gpu-server-card">
              <div className="gpu-server-header">
                <div>
                  <div className="gpu-server-name" style={{ display:'flex', alignItems:'center', gap:8 }}>
                    {gs.server_name}
                    {/* Live agent health — Offline means the node agent is down, so this
                        server can't actually be used regardless of stored instance counts. */}
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700,
                      padding:'2px 8px', borderRadius:20,
                      background: gs.agent_online ? 'var(--color-success-50)' : 'var(--color-danger-50)',
                      color: gs.agent_online ? 'var(--color-success-500)' : 'var(--color-danger-500)' }}>
                      <span style={{ width:6, height:6, borderRadius:'50%',
                        background: gs.agent_online ? 'var(--color-success-500)' : 'var(--color-danger-500)' }} />
                      {gs.agent_online ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  <div className="gpu-server-ip">{gs.server_ip}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {gs.metrics_stale && gs.agent_online && <span className="gpu-badge mig-cap" title="GPU metrics are stale (>5 min old)">metrics stale</span>}
                  {gs.mig_enabled && <span className="gpu-badge mig">MIG</span>}
                  {gs.mig_capable && !gs.mig_enabled && <span className="gpu-badge mig-cap">MIG-capable</span>}
                </div>
              </div>

              <div className="gpu-model-row">
                <span className="gpu-model-name">{gs.gpu_model || 'Unknown GPU'}</span>
                <span className="gpu-count-badge">{gs.gpu_count}× GPU</span>
              </div>

              <div className="gpu-specs">
                <div className="gpu-spec"><span>VRAM</span><strong>{gs.total_vram_gb}GB</strong></div>
                <div className="gpu-spec"><span>CUDA</span><strong>{gs.cuda_version || '—'}</strong></div>
                <div className="gpu-spec"><span>Driver</span><strong>{gs.driver_version || '—'}</strong></div>
                {gs.cuda_cores && <div className="gpu-spec"><span>Cores</span><strong>{gs.cuda_cores.toLocaleString()}</strong></div>}
              </div>

              {/* Live metrics from nvidia-smi */}
              {metrics.length > 0 && (
                <div className="gpu-metrics-section">
                  {metrics.map((m, i) => {
                    const used = Number(m.memory_used_mb) || 0;
                    const total = Number(m.memory_total_mb) || 0;
                    const memPct = total ? Math.round((used / total) * 100) : (Number(m.utilization_mem) || 0);
                    return (
                    <div key={i} className="gpu-metric-gpu">
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>GPU {m.index}: {m.name}</div>
                      <GpuBar pct={Number(m.utilization_gpu)||0} label="GPU" />
                      <GpuBar pct={memPct} label="VRAM" />
                      <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 11, color: 'var(--muted)' }}>
                        <span>🌡 {m.temperature}°C</span>
                        <span>⚡ {m.power_draw}W</span>
                        <span>💾 {(used/1024).toFixed(1)}/{Math.round(total/1024)}GB</span>
                      </div>
                    </div>
                    );
                  })}
                  {gs.gpu_metrics_at && (
                    <div style={{ fontSize: 10, color: 'var(--muted-2)', marginTop: 4 }}>
                      Updated {new Date(gs.gpu_metrics_at).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              )}

              {/* Instances */}
              <GpuInstanceList
                gpuServerId={gs.id}
                canCreate={canCreate}
                onAllocate={(instId, instName, vram) =>
                  onAllocate({ gpuServerId: gs.id, instanceId: instId, instanceName: instName, vramGb: vram })}
              />

              <div className="gpu-server-actions">
                <button className="btn btn-secondary btn-sm"
                  onClick={() => onRefresh(gs.id)}>⟳ Refresh Metrics</button>
                {isAdmin && canEdit && (
                  <button className="btn btn-secondary btn-sm" style={{ color: 'var(--color-warning-500)' }}
                    title="Free instances stuck as 'provisioned' with no active allocation"
                    onClick={() => onReleaseStuck(gs.id)}>🔓 Release Stuck</button>
                )}
                {isAdmin && canDelete && (
                  <button className="btn btn-tinted-danger btn-sm"
                    onClick={() => onDelete(gs.id)}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
